// Research Data for FEMSA Colas Mexico - TIER 1 Questions
// Dummy data with 11 demographic cuts

export interface DemographicCut {
  label: string;
  value: number;
}

export interface Answer {
  label: string;
  total_pct: number;
  by_age: DemographicCut[];
  by_gender: DemographicCut[];
  by_region: DemographicCut[];
  by_urban_rural: DemographicCut[];
  by_nse: DemographicCut[];
  by_education: DemographicCut[];
  by_household: DemographicCut[];
  by_children: DemographicCut[];
  by_employment: DemographicCut[];
  by_occupation: DemographicCut[];
  by_housing: DemographicCut[];
}

export type SourceType = "synthetic" | "human" | "hybrid";

export interface ResearchQuestion {
  id: string;
  category: string;
  section: string;
  question: string;
  questionType: "single" | "multiple";
  answers: Answer[];
  // Metadata
  dateAsked: string; // ISO date string
  sourceType: SourceType;
  sampleSize: number;
}

// Demographic dimension labels
export const demographicDimensions = [
  { id: "by_age", label: "Age" },
  { id: "by_gender", label: "Gender" },
  { id: "by_region", label: "Region" },
  { id: "by_urban_rural", label: "Urban/Rural" },
  { id: "by_nse", label: "NSE (Income)" },
  { id: "by_education", label: "Education" },
  { id: "by_household", label: "Household" },
  { id: "by_children", label: "Children" },
  { id: "by_employment", label: "Employment" },
  { id: "by_occupation", label: "Occupation" },
  { id: "by_housing", label: "Housing" },
] as const;

// Helper to generate dummy demographic cuts
const generateDemoCuts = (basePct: number): Omit<Answer, "label" | "total_pct"> => ({
  by_age: [
    { label: "18-24", value: basePct + Math.random() * 10 - 5 },
    { label: "25-34", value: basePct + Math.random() * 10 - 5 },
    { label: "35-44", value: basePct + Math.random() * 10 - 5 },
    { label: "45-54", value: basePct + Math.random() * 10 - 5 },
    { label: "55+", value: basePct + Math.random() * 10 - 5 },
  ],
  by_gender: [
    { label: "Male", value: basePct + Math.random() * 8 - 4 },
    { label: "Female", value: basePct + Math.random() * 8 - 4 },
  ],
  by_region: [
    { label: "Norte", value: basePct + Math.random() * 12 - 6 },
    { label: "Bajio", value: basePct + Math.random() * 12 - 6 },
    { label: "Centro", value: basePct + Math.random() * 12 - 6 },
    { label: "Sur", value: basePct + Math.random() * 12 - 6 },
    { label: "CDMX", value: basePct + Math.random() * 12 - 6 },
  ],
  by_urban_rural: [
    { label: "Urban", value: basePct + Math.random() * 6 - 3 },
    { label: "Rural", value: basePct + Math.random() * 6 - 3 },
  ],
  by_nse: [
    { label: "A/B", value: basePct + Math.random() * 15 - 7 },
    { label: "C+", value: basePct + Math.random() * 10 - 5 },
    { label: "C", value: basePct + Math.random() * 8 - 4 },
    { label: "C-", value: basePct + Math.random() * 8 - 4 },
    { label: "D+", value: basePct + Math.random() * 10 - 5 },
    { label: "D/E", value: basePct + Math.random() * 15 - 7 },
  ],
  by_education: [
    { label: "Primary", value: basePct + Math.random() * 10 - 5 },
    { label: "Secondary", value: basePct + Math.random() * 8 - 4 },
    { label: "Preparatoria", value: basePct + Math.random() * 6 - 3 },
    { label: "University", value: basePct + Math.random() * 10 - 5 },
  ],
  by_household: [
    { label: "1 person", value: basePct + Math.random() * 8 - 4 },
    { label: "2 people", value: basePct + Math.random() * 6 - 3 },
    { label: "3-4 people", value: basePct + Math.random() * 6 - 3 },
    { label: "5+ people", value: basePct + Math.random() * 8 - 4 },
  ],
  by_children: [
    { label: "No children", value: basePct + Math.random() * 8 - 4 },
    { label: "Children <6", value: basePct + Math.random() * 10 - 5 },
    { label: "Children 6-12", value: basePct + Math.random() * 8 - 4 },
    { label: "Children 13+", value: basePct + Math.random() * 8 - 4 },
  ],
  by_employment: [
    { label: "Employed", value: basePct + Math.random() * 6 - 3 },
    { label: "Self-employed", value: basePct + Math.random() * 8 - 4 },
    { label: "Unemployed", value: basePct + Math.random() * 10 - 5 },
    { label: "Student", value: basePct + Math.random() * 12 - 6 },
    { label: "Retired", value: basePct + Math.random() * 10 - 5 },
  ],
  by_occupation: [
    { label: "Professional", value: basePct + Math.random() * 10 - 5 },
    { label: "Technical", value: basePct + Math.random() * 8 - 4 },
    { label: "Services", value: basePct + Math.random() * 6 - 3 },
    { label: "Commerce", value: basePct + Math.random() * 8 - 4 },
    { label: "Other", value: basePct + Math.random() * 10 - 5 },
  ],
  by_housing: [
    { label: "Owner", value: basePct + Math.random() * 8 - 4 },
    { label: "Renter", value: basePct + Math.random() * 10 - 5 },
    { label: "Family home", value: basePct + Math.random() * 8 - 4 },
  ],
});

// S6-4: Packaging & Retornable Questions (TIER 1)
const s64Questions: ResearchQuestion[] = [
  {
    id: "S6-4-Q5",
    category: "Retornable Perception",
    section: "S6-4: Packaging & Retornable",
    question: "Participa en el programa de botellas RETORNABLES?",
    questionType: "single",
    dateAsked: "2026-01-15",
    sourceType: "hybrid",
    sampleSize: 1247,
    answers: [
      { label: "Si, regularmente", total_pct: 34.2, ...generateDemoCuts(34.2) },
      { label: "Si, ocasionalmente", total_pct: 28.5, ...generateDemoCuts(28.5) },
      { label: "No, pero lo he considerado", total_pct: 18.7, ...generateDemoCuts(18.7) },
      { label: "No, nunca", total_pct: 18.6, ...generateDemoCuts(18.6) },
    ],
  },
  {
    id: "S6-4-Q6",
    category: "Retornable Perception",
    section: "S6-4: Packaging & Retornable",
    question: "Por que participa (o participaria) en retornables? (seleccione hasta 3)",
    questionType: "multiple",
    dateAsked: "2026-01-15",
    sourceType: "hybrid",
    sampleSize: 1247,
    answers: [
      { label: "Menor precio por litro", total_pct: 67.3, ...generateDemoCuts(67.3) },
      { label: "Mejor sabor (vidrio)", total_pct: 45.8, ...generateDemoCuts(45.8) },
      { label: "Responsabilidad ambiental", total_pct: 38.2, ...generateDemoCuts(38.2) },
      { label: "Tradicion familiar", total_pct: 31.5, ...generateDemoCuts(31.5) },
      { label: "Tamano conveniente", total_pct: 22.4, ...generateDemoCuts(22.4) },
    ],
  },
  {
    id: "S6-4-Q7",
    category: "Retornable Perception",
    section: "S6-4: Packaging & Retornable",
    question: "Por que NO participa en retornables? (seleccione hasta 3)",
    questionType: "multiple",
    dateAsked: "2026-01-15",
    sourceType: "synthetic",
    sampleSize: 2500,
    answers: [
      { label: "Inconveniencia de devolver", total_pct: 52.1, ...generateDemoCuts(52.1) },
      { label: "Deposito requerido", total_pct: 41.3, ...generateDemoCuts(41.3) },
      { label: "Peso al cargar", total_pct: 38.7, ...generateDemoCuts(38.7) },
      { label: "Almacenamiento de vacios", total_pct: 35.2, ...generateDemoCuts(35.2) },
      { label: "No disponible en mi tienda", total_pct: 28.9, ...generateDemoCuts(28.9) },
    ],
  },
  {
    id: "S6-4-Q4",
    category: "Retornable Perception",
    section: "S6-4: Packaging & Retornable",
    question: "Que tipo de empaque prefiere para refrescos?",
    questionType: "single",
    dateAsked: "2026-01-12",
    sourceType: "human",
    sampleSize: 856,
    answers: [
      { label: "Vidrio retornable", total_pct: 31.4, ...generateDemoCuts(31.4) },
      { label: "PET plastico", total_pct: 42.8, ...generateDemoCuts(42.8) },
      { label: "Lata de aluminio", total_pct: 18.3, ...generateDemoCuts(18.3) },
      { label: "Vidrio no retornable", total_pct: 7.5, ...generateDemoCuts(7.5) },
    ],
  },
  {
    id: "S6-4-Q8",
    category: "Retornable Perception",
    section: "S6-4: Packaging & Retornable",
    question: "Cree que los refrescos saben diferente segun el empaque?",
    questionType: "single",
    dateAsked: "2026-01-12",
    sourceType: "human",
    sampleSize: 856,
    answers: [
      { label: "Si, definitivamente", total_pct: 48.2, ...generateDemoCuts(48.2) },
      { label: "Si, un poco", total_pct: 27.6, ...generateDemoCuts(27.6) },
      { label: "No, saben igual", total_pct: 24.2, ...generateDemoCuts(24.2) },
    ],
  },
];

// S3-5: Brand Media Touchpoints Questions (TIER 1)
const s35Questions: ResearchQuestion[] = [
  {
    id: "S3-5-Q2",
    category: "Media & Communication",
    section: "S3-5: Brand Media Touchpoints",
    question: "Que plataformas de redes sociales usa regularmente? (seleccione todas)",
    questionType: "multiple",
    dateAsked: "2026-01-20",
    sourceType: "synthetic",
    sampleSize: 3000,
    answers: [
      { label: "Facebook", total_pct: 78.4, ...generateDemoCuts(78.4) },
      { label: "WhatsApp", total_pct: 92.1, ...generateDemoCuts(92.1) },
      { label: "Instagram", total_pct: 54.3, ...generateDemoCuts(54.3) },
      { label: "TikTok", total_pct: 41.7, ...generateDemoCuts(41.7) },
      { label: "YouTube", total_pct: 68.9, ...generateDemoCuts(68.9) },
      { label: "Twitter/X", total_pct: 23.5, ...generateDemoCuts(23.5) },
    ],
  },
  {
    id: "S3-5-Q7",
    category: "Media & Communication",
    section: "S3-5: Brand Media Touchpoints",
    question: "Donde descubre nuevos productos/marcas? (seleccione hasta 3)",
    questionType: "multiple",
    dateAsked: "2026-01-20",
    sourceType: "synthetic",
    sampleSize: 3000,
    answers: [
      { label: "En la tienda/punto de venta", total_pct: 62.8, ...generateDemoCuts(62.8) },
      { label: "Redes sociales", total_pct: 54.2, ...generateDemoCuts(54.2) },
      { label: "Television", total_pct: 47.3, ...generateDemoCuts(47.3) },
      { label: "Recomendacion de amigos/familia", total_pct: 43.6, ...generateDemoCuts(43.6) },
      { label: "Publicidad exterior", total_pct: 28.4, ...generateDemoCuts(28.4) },
      { label: "Radio", total_pct: 15.7, ...generateDemoCuts(15.7) },
    ],
  },
  {
    id: "S3-5-Q10",
    category: "Media & Communication",
    section: "S3-5: Brand Media Touchpoints",
    question: "Donde ha visto/escuchado publicidad de Coca-Cola? (seleccione todas)",
    questionType: "multiple",
    dateAsked: "2026-01-22",
    sourceType: "hybrid",
    sampleSize: 1850,
    answers: [
      { label: "Television", total_pct: 87.2, ...generateDemoCuts(87.2) },
      { label: "Redes sociales", total_pct: 71.5, ...generateDemoCuts(71.5) },
      { label: "Punto de venta", total_pct: 68.3, ...generateDemoCuts(68.3) },
      { label: "Espectaculares/OOH", total_pct: 54.6, ...generateDemoCuts(54.6) },
      { label: "YouTube", total_pct: 48.2, ...generateDemoCuts(48.2) },
      { label: "Radio", total_pct: 32.1, ...generateDemoCuts(32.1) },
    ],
  },
  {
    id: "S3-5-Q13",
    category: "Media & Communication",
    section: "S3-5: Brand Media Touchpoints",
    question: "Donde ha visto/escuchado publicidad de Aga - Red Cola? (seleccione todas)",
    questionType: "multiple",
    dateAsked: "2026-01-22",
    sourceType: "hybrid",
    sampleSize: 1850,
    answers: [
      { label: "Punto de venta", total_pct: 45.3, ...generateDemoCuts(45.3) },
      { label: "Television", total_pct: 38.7, ...generateDemoCuts(38.7) },
      { label: "Redes sociales", total_pct: 28.4, ...generateDemoCuts(28.4) },
      { label: "Espectaculares/OOH", total_pct: 22.1, ...generateDemoCuts(22.1) },
      { label: "No he visto publicidad", total_pct: 35.6, ...generateDemoCuts(35.6) },
    ],
  },
  {
    id: "S3-5-Q14",
    category: "Media & Communication",
    section: "S3-5: Brand Media Touchpoints",
    question: "Que marcas de refresco recuerda haber visto anunciadas en las ultimas 4 semanas?",
    questionType: "multiple",
    dateAsked: "2026-01-25",
    sourceType: "human",
    sampleSize: 1024,
    answers: [
      { label: "Coca-Cola", total_pct: 89.3, ...generateDemoCuts(89.3) },
      { label: "Pepsi", total_pct: 62.8, ...generateDemoCuts(62.8) },
      { label: "Aga - Red Cola", total_pct: 34.5, ...generateDemoCuts(34.5) },
      { label: "Big Cola", total_pct: 28.7, ...generateDemoCuts(28.7) },
      { label: "Ninguna", total_pct: 5.2, ...generateDemoCuts(5.2) },
    ],
  },
];

// S7-3: Attribute Importance Questions (TIER 1)
const s73Questions: ResearchQuestion[] = [
  {
    id: "S7-3-Q1",
    category: "Decision Drivers",
    section: "S7-3: Attribute Importance",
    question: "Que tan importante es el PRECIO al elegir un refresco?",
    questionType: "single",
    dateAsked: "2026-01-28",
    sourceType: "human",
    sampleSize: 1500,
    answers: [
      { label: "Muy importante", total_pct: 58.4, ...generateDemoCuts(58.4) },
      { label: "Algo importante", total_pct: 28.7, ...generateDemoCuts(28.7) },
      { label: "Ni importante ni no importante", total_pct: 8.3, ...generateDemoCuts(8.3) },
      { label: "Poco importante", total_pct: 3.2, ...generateDemoCuts(3.2) },
      { label: "Nada importante", total_pct: 1.4, ...generateDemoCuts(1.4) },
    ],
  },
  {
    id: "S7-3-Q2",
    category: "Decision Drivers",
    section: "S7-3: Attribute Importance",
    question: "Que tan importante es la MARCA al elegir un refresco?",
    questionType: "single",
    dateAsked: "2026-01-28",
    sourceType: "human",
    sampleSize: 1500,
    answers: [
      { label: "Muy importante", total_pct: 52.1, ...generateDemoCuts(52.1) },
      { label: "Algo importante", total_pct: 31.4, ...generateDemoCuts(31.4) },
      { label: "Ni importante ni no importante", total_pct: 10.2, ...generateDemoCuts(10.2) },
      { label: "Poco importante", total_pct: 4.5, ...generateDemoCuts(4.5) },
      { label: "Nada importante", total_pct: 1.8, ...generateDemoCuts(1.8) },
    ],
  },
  {
    id: "S7-3-Q3",
    category: "Decision Drivers",
    section: "S7-3: Attribute Importance",
    question: "Que tan importante es el TAMANO/FORMATO al elegir un refresco?",
    questionType: "single",
    dateAsked: "2026-01-30",
    sourceType: "synthetic",
    sampleSize: 5000,
    answers: [
      { label: "Muy importante", total_pct: 41.3, ...generateDemoCuts(41.3) },
      { label: "Algo importante", total_pct: 35.8, ...generateDemoCuts(35.8) },
      { label: "Ni importante ni no importante", total_pct: 14.6, ...generateDemoCuts(14.6) },
      { label: "Poco importante", total_pct: 5.7, ...generateDemoCuts(5.7) },
      { label: "Nada importante", total_pct: 2.6, ...generateDemoCuts(2.6) },
    ],
  },
  {
    id: "S7-3-Q4",
    category: "Decision Drivers",
    section: "S7-3: Attribute Importance",
    question: "Que tan importante es el TIPO (regular, zero, light) al elegir un refresco?",
    questionType: "single",
    dateAsked: "2026-01-30",
    sourceType: "synthetic",
    sampleSize: 5000,
    answers: [
      { label: "Muy importante", total_pct: 35.7, ...generateDemoCuts(35.7) },
      { label: "Algo importante", total_pct: 28.4, ...generateDemoCuts(28.4) },
      { label: "Ni importante ni no importante", total_pct: 18.9, ...generateDemoCuts(18.9) },
      { label: "Poco importante", total_pct: 10.3, ...generateDemoCuts(10.3) },
      { label: "Nada importante", total_pct: 6.7, ...generateDemoCuts(6.7) },
    ],
  },
];

// Export all research questions
export const researchQuestions: ResearchQuestion[] = [
  ...s64Questions,
  ...s35Questions,
  ...s73Questions,
];

// Get unique categories
export const researchCategories = [
  ...new Set(researchQuestions.map((q) => q.category)),
];

// Get unique sections
export const researchSections = [
  ...new Set(researchQuestions.map((q) => q.section)),
];
